import { useEffect, useState } from "react";
import { useGetList } from "react-admin";

/**
 * Supported reference-code domains. Matches the `/ref-codes/{codeType}` API segment.
 */
export type RefCodeType = "icd" | "cpt" | "ndc";

/**
 * Normalized option surfaced to consumers (e.g. AutocompleteCodeInput).
 *
 * The upstream API returns a different description field per codeType
 * (`description` / `short_description` / `product_name`). This hook
 * flattens them to a single `description` key so downstream UI stays simple.
 *
 * `id` is set to `code` to satisfy react-admin's cache, which requires
 * every record to have an `id` field.
 */
export interface RefCodeOption {
  id: string;
  code: string;
  description: string;
}

export interface UseRefCodeSearchResult {
  options: RefCodeOption[];
  loading: boolean;
  search: string;
  setSearch: (value: string) => void;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

/** Map codeType -> the field on the API response that holds the human-readable description. */
const descriptionField = (codeType: RefCodeType): string => {
  switch (codeType) {
    case "cpt":
      return "short_description";
    case "ndc":
      return "product_name";
    case "icd":
    default:
      return "description";
  }
};

/**
 * Debounced ref-code search hook.
 *
 * Backed by `GET /ref-codes/{codeType}?q=<query>&limit=20` via the react-admin
 * data provider. Queries shorter than 2 non-whitespace characters do not hit
 * the network. Errors are swallowed (the hook returns `{ options: [], loading: false }`)
 * so callers can render a freeSolo fallback without extra guard logic.
 *
 * @example
 *   const { options, loading, search, setSearch } = useRefCodeSearch("icd");
 *   <Autocomplete options={options} onInputChange={(_, v) => setSearch(v)} />
 */
export const useRefCodeSearch = (codeType: RefCodeType): UseRefCodeSearchResult => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const trimmed = debounced.trim();
  const enabled = trimmed.length >= MIN_QUERY_LEN;
  const descKey = descriptionField(codeType);

  const { data, isLoading, error } = useGetList(
    `ref-codes/${codeType}`,
    {
      filter: { q: trimmed },
      pagination: { page: 1, perPage: 20 },
      sort: { field: "relevance", order: "DESC" },
    },
    { enabled }
  );

  const options: RefCodeOption[] =
    error || !data
      ? []
      : data.map((row: Record<string, unknown>) => ({
          id: String(row.code ?? ""),
          code: String(row.code ?? ""),
          description: String(row[descKey] ?? ""),
        }));

  return {
    options,
    loading: enabled && isLoading,
    search,
    setSearch,
  };
};
